import React, { useCallback, useEffect, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import {
  NotificationTypes,
  errorNotificationBuilder,
  successNotificationBuilder,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { bulkAssignRoleResourceToPrincipal } from '@atlassian/temp-proxies';
import { AddContactFields } from '@css-xp/ppc-add-contact-fields';
import { failureMessage } from '@css-xp/ppc-common';
import './add-customer-contact.scss';

interface AddCustomerContactProps {
  entitlementId: string;
  txaId: string;
  currentUserList: string[];
  refetch?: () => void;
}

export const AddCustomerContact = ({ entitlementId, txaId, currentUserList, refetch }: AddCustomerContactProps) => {
  const { closeSidebar } = useSidebar();
  const { notificationSent, sendSuccessNotification, setNotificationSent, sendErrorNotification } = useNotification();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [atlassianAccountIds, setAtlassianAccountIds] = useState<Array<string>>(['']);
  const [atlassianAccountEmails, setAtlassianAccountEmails] = useState<Array<string>>(['']);

  const handleFormSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        assignments: atlassianAccountIds.map((id) => {
          return {
            role: 'end-customer-contact',
            resourceAri: `ari:cloud:commerce::entitlement/${entitlementId}`,
            userId: id,
          };
        }),
      };
      await bulkAssignRoleResourceToPrincipal(requestPayload, txaId);
      const commonMessage = `You've added ${atlassianAccountEmails.length} customer contacts`;
      const message = (
        <div>
          {commonMessage}:
          <ul className="mt-4">
            {atlassianAccountEmails?.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        </div>
      );
      if (!notificationSent) {
        const notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update successful')
          .withMessage(message)
          .withDismissAfter(5000)
          .build();
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
      setIsLoading(false);
      closeSidebar();
      if (refetch) {
        refetch();
      }
    } catch (err) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update unsuccessful')
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      setIsLoading(false);
    }
  }, [
    atlassianAccountIds,
    atlassianAccountEmails,
    closeSidebar,
    entitlementId,
    notificationSent,
    refetch,
    sendErrorNotification,
    sendSuccessNotification,
    setNotificationSent,
    txaId,
  ]);

  useEffect(() => {
    if (atlassianAccountIds.findIndex((id) => !id) >= 0) {
      setIsSaveDisabled(true);
    } else {
      setIsSaveDisabled(false);
    }
  }, [atlassianAccountIds]);

  return (
    <div className="add-ecc-subscription-details-page">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={'Add customer contact'} />
            <FormSection>
              <h4 className="customer-contact-text">Customer contact</h4>
              <AddContactFields
                setAtlassianAccountIds={setAtlassianAccountIds}
                atlassianAccountIds={atlassianAccountIds}
                setAtlassianAccountEmails={setAtlassianAccountEmails}
                atlassianAccountEmails={atlassianAccountEmails}
                currentUserList={currentUserList}
                userAlreadyExistText="Customer contact already exists"
                emailFieldText="Enter contact email"
                nameFieldText="Enter contact full name"
                addButtonText="Add customer contact"
              />
            </FormSection>
            <div className="form-footer">
              <FormFooter align="end">
                <ButtonGroup>
                  <Button appearance="subtle" onClick={closeSidebar}>
                    Cancel
                  </Button>
                  <LoadingButton
                    appearance="primary"
                    isLoading={isLoading}
                    isDisabled={isSaveDisabled}
                    type="submit"
                    testId="save-customer-contact-button"
                  >
                    Save
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};
